import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import {
  gerarListaAtivos,
  exportarListaAtivos,
  listarGesListaAtivos,
} from '~/Services/ListaAtivosService';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { schemaPorQtipo } from './validationSchemas';
import SidebarFiltros from './components/SidebarFiltros';
import CardGeracao from './components/CardGeracao';
import CardResultado from './components/CardResultado';
import { Container, Card, Button } from 'react-bootstrap';
import { FaFilter, FaInfoCircle } from 'react-icons/fa';

const initialValues = {
  geId: null,
  grupoGeId: null,
  tipos: ['0', '1', '2', '3', '4', '5'],
  status: '0',
  modoListagem: 0,
  qtipoTab: 'codigo',
  qtipo: 0,
  codIni: null,
  codFim: null,
  nomeIni: '',
  nomeFim: '',
  conversaoMoeda: false,
  moedaCodigo: null,
  cotacoes: { usd: 0, eur: 0, gbp: 0 },
  exibirImobilizado: false,
  page: 1,
  pageSize: 50,
};

const ListaAtivos = () => {
  const gepadrao = useSelector((state) => state.auth.user.gepadrao);

  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showFiltros, setShowFiltros] = useState(false);
  const [gesOpcoes, setGesOpcoes] = useState([]);
  const [loadingGes, setLoadingGes] = useState(true);
  const [gePadraoInicial, setGePadraoInicial] = useState(null);

  // Carrega GEs e calcula auto-seleção (1 única opção OU gepadrao do redux)
  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        const lista = await listarGesListaAtivos();
        if (!ativo) return;
        const opcoes = (lista || []).map((g) => ({
          value: g.codigo_grupo_empresa,
          label: g.apelido,
        }));
        setGesOpcoes(opcoes);

        let geInicial = null;
        if (opcoes.length === 1) {
          geInicial = opcoes[0].value;
        } else if (gepadrao) {
          const encontrado = opcoes.find((g) => g.value === gepadrao);
          if (encontrado) geInicial = encontrado.value;
        }
        setGePadraoInicial(geInicial);
      } catch (err) {
        if (ativo) toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setLoadingGes(false);
      }
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, [gepadrao]);

  const handleGerar = async (values, { setFieldValue }) => {
    setLoading(true);
    setFieldValue('page', 1);
    try {
      const data = await gerarListaAtivos({ ...values, page: 1 });
      setResultado(data);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMudarPagina = async (values, pagina, setFieldValue) => {
    setLoading(true);
    setFieldValue('page', pagina);
    try {
      const data = await gerarListaAtivos({ ...values, page: pagina });
      setResultado(data);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async (values) => {
    setLoadingExport(true);
    try {
      const data = await exportarListaAtivos(values);
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'lista-ativos.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <Container fluid className='my-3'>
      <Formik
        initialValues={initialValues}
        validationSchema={(values) => schemaPorQtipo[values ? values.qtipo : 0]}
        onSubmit={handleGerar}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formik) => {
          const geValor =
            gesOpcoes.find((g) => g.value === formik.values.geId) || null;
          const escopoDefinido =
            formik.values.geId != null || formik.values.grupoGeId != null;
          return (
            <>
              <AutoSelecionarGePadrao
                formik={formik}
                gePadraoInicial={gePadraoInicial}
              />
              <HeaderAzul
                title="Lista de Ativos"
                contemSelector
                options={gesOpcoes}
                selectorValue={geValor}
                selectorPlaceholder="Selecione um GE..."
                selectorLoading={loadingGes}
                selectorIsClearable
                selectorInputId="header-ge-lista-ativos"
                onSelectorChange={(opcao) => {
                  formik.setFieldValue('geId', opcao ? opcao.value : null);
                  formik.setFieldValue('grupoGeId', null);
                  setResultado(null);
                }}
              />
              <Form>
                <SidebarFiltros
                  formik={formik}
                  isOpen={showFiltros}
                  onClose={() => setShowFiltros(false)}
                />
                {escopoDefinido ? (
                  <>
                    <CardGeracao
                      formik={formik}
                      onAbrirFiltros={() => setShowFiltros(true)}
                      onLimpar={() => {
                        formik.setFieldValue('codIni', null);
                        formik.setFieldValue('codFim', null);
                        formik.setFieldValue('nomeIni', '');
                        formik.setFieldValue('nomeFim', '');
                        formik.setFieldValue('qtipoTab', 'codigo');
                        formik.setFieldValue('qtipo', 0);
                        formik.setFieldValue('tipos', [
                          '0',
                          '1',
                          '2',
                          '3',
                          '4',
                          '5',
                        ]);
                        formik.setFieldValue('status', '0');
                        formik.setFieldValue('modoListagem', 0);
                        formik.setFieldValue('exibirImobilizado', false);
                        formik.setFieldValue('conversaoMoeda', false);
                        formik.setFieldValue('moedaCodigo', null);
                        formik.setFieldValue('cotacoes', {
                          usd: 0,
                          eur: 0,
                          gbp: 0,
                        });
                        formik.setErrors({});
                        formik.setTouched({});
                        setResultado(null);
                      }}
                    />
                    <CardResultado
                      resultado={resultado}
                      loading={loading}
                      loadingExport={loadingExport}
                      formik={formik}
                      onMudarPagina={(pagina) =>
                        handleMudarPagina(
                          formik.values,
                          pagina,
                          formik.setFieldValue
                        )
                      }
                      onExportar={() => handleExportar(formik.values)}
                    />
                  </>
                ) : (
                  <Card className="text-center shadow-sm mb-3 py-5">
                    <Card.Body>
                      <FaInfoCircle
                        size={48}
                        className="text-muted mb-3"
                      />
                      <h5 className="text-muted mb-2">
                        Selecione um GE para começar
                      </h5>
                      <p className="text-muted mb-3">
                        Escolha um GE no cabeçalho ou abra os filtros para
                        selecionar um Grupo de GE
                      </p>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        onClick={() => setShowFiltros(true)}
                      >
                        <FaFilter className="mr-1" />
                        Abrir filtros
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </Form>
            </>
          );
        }}
      </Formik>
    </Container>
  );
};

// Aplica geId padrão assim que `gePadraoInicial` chega, sem sobrescrever escolha do usuário
const AutoSelecionarGePadrao = ({ formik, gePadraoInicial }) => {
  const [aplicado, setAplicado] = useState(false);
  useEffect(() => {
    if (aplicado) return;
    if (gePadraoInicial == null) return;
    if (formik.values.geId != null || formik.values.grupoGeId != null) {
      setAplicado(true);
      return;
    }
    formik.setFieldValue('geId', gePadraoInicial);
    setAplicado(true);
  }, [gePadraoInicial, aplicado, formik]);
  return null;
};

export default ListaAtivos;
